/**
 * 大海龟旅店 — 姿态导演：替每个角色记住「休息姿态」（坐 / 擦杯 / 站），把台词、表情、零星动作翻译成图集真有的帧。
 *  - add()      登记角色（坐着的人 sit，老板 wipe，其余 idle）并立刻摆好
 *  - line()     一句台词开始：说话人 sittalk / talk / boast，上一位说话人回到休息姿态；end() 收尾
 *  - emote()    表情：有身体帧就用帧（laugh / dim / drink …），并告诉调用方还要不要弹气泡
 *  - tick()     固定步：到期的姿态归位；坐着的酒客零星举杯（SeededRng，确定性）
 *  - laughNear() 闲聊哄笑：声源附近有 laugh 帧的人一起笑
 * 只依赖 characters 的 Actor（纹理 key + play(state)），其它 IActor 实现（降级演员）静默跳过。
 */
import type Phaser from 'phaser';
import type { Actor } from '@/characters';
import { animKey } from '@/characters/anims';
import { SeededRng } from '@/core/rng';
import type { IActor } from '@/core/types/contracts';
import { DRINK_EVERY_MS, DRINK_MS, LAUGH_MS, POSE_FOR_EMOTE, TABLE_LAUGH_RADIUS, pickPose, restPose, talkChain, type PoseChain } from './innPoses';

interface Slot { actor: Actor; seated: boolean; rest: string; until: number; nextDrink: number; talking: boolean }

/** characters 的 Actor（有纹理 key 与 play(任意状态)）。 */
export function isPoseActor(a: IActor | null | undefined): a is Actor {
  const o = a as unknown as Record<string, unknown> | null | undefined;
  return !!o && typeof o['texture'] === 'string' && typeof o['play'] === 'function' && typeof o['setVelocity'] === 'function';
}

export class InnPoseDirector {
  private readonly slots = new Map<string, Slot>();
  private readonly rng: SeededRng;
  private now = 0;
  private speaker: string | null = null;

  constructor(private readonly scene: Phaser.Scene, seed: number | string) {
    this.rng = new SeededRng(SeededRng.hash(`${seed}:inn-poses`));
  }

  // ------------------------------------------------------------------ registry
  add(id: string, actor: IActor, seated: boolean): void {
    if (!isPoseActor(actor)) return;
    const slot: Slot = { actor, seated, rest: restPose(id, seated), until: 0, nextDrink: this.now + this.rng.range(DRINK_EVERY_MS[0], DRINK_EVERY_MS[1]), talking: false };
    this.slots.set(id, slot);
    this.restore(id);
  }
  remove(id: string): void { this.slots.delete(id); }
  has(id: string): boolean { return this.slots.has(id); }
  isSeated(id: string): boolean { return this.slots.get(id)?.seated ?? false; }

  /** 时间线 `anim` 步：新的休息姿态（sit = 落座，idle = 站起，dim = 保持暗淡）。 */
  setRest(id: string, state: string): void {
    const slot = this.slots.get(id);
    if (!slot) return;
    slot.seated = state === 'sit' || state === 'sittalk' || state === 'dim' || state === 'drink';
    slot.rest = state === 'idle' ? restPose(id, false) : state;
    this.restore(id);
  }

  // ------------------------------------------------------------------ poses
  private hasState(slot: Slot, state: string): boolean {
    return this.scene.anims.exists(animKey(slot.actor.texture, state, slot.actor.facing));
  }
  private pick(slot: Slot, chain: PoseChain): string | null { return pickPose((s) => this.hasState(slot, s), chain); }

  /** 回到休息姿态（图集没有该帧时 characters 的回退链兜底）。 */
  restore(id: string): void {
    const slot = this.slots.get(id);
    if (!slot) return;
    slot.until = 0;
    slot.talking = false;
    slot.actor.play(slot.rest);
  }

  /** 表情：返回是否还需要气泡（有身体帧且表情属于 `fallback` 类时不弹）。 */
  emote(id: string, emote: string, ms?: number): { bubble: boolean; pose: string | null } {
    const slot = this.slots.get(id);
    const rule = POSE_FOR_EMOTE[emote];
    if (!slot || !rule) return { bubble: true, pose: null };
    const pose = this.pick(slot, slot.seated ? rule.seated : rule.standing);
    if (!pose) return { bubble: true, pose: null };
    if (rule.hold) { slot.rest = pose; slot.until = 0; }
    else slot.until = this.now + (ms && ms > 0 ? ms : 1200);
    slot.talking = false;
    slot.actor.play(pose);
    return { bubble: rule.bubble === 'always', pose };
  }

  /** 一句台词开始（旁白 speaker 为空 → 只让上一位说话人归位）。 */
  line(speaker: string | undefined, emotion?: string): void {
    if (this.speaker && this.speaker !== speaker) this.restore(this.speaker);
    this.speaker = speaker || null;
    const slot = speaker ? this.slots.get(speaker) : undefined;
    if (!slot) return;
    if (slot.until > this.now) return; // 定时姿态（笑、举杯）还在播：不打断
    const pose = this.pick(slot, talkChain(slot.seated, emotion));
    if (!pose || pose === slot.rest) return;
    slot.talking = true;
    slot.until = 0;
    slot.actor.play(pose);
  }

  /** 对白结束：说话人归位。 */
  end(): void {
    if (this.speaker) this.restore(this.speaker);
    this.speaker = null;
  }

  /** 声源附近有 laugh 帧的人一起笑；返回笑的人数。 */
  laughNear(x: number, y: number, radius = TABLE_LAUGH_RADIUS): number {
    let n = 0;
    for (const slot of this.slots.values()) {
      if (slot.talking || slot.until > this.now) continue;
      const dx = slot.actor.x - x, dy = slot.actor.y - y;
      if (dx * dx + dy * dy > radius * radius) continue;
      if (!this.hasState(slot, 'laugh')) continue;
      slot.until = this.now + LAUGH_MS;
      slot.actor.play('laugh');
      n++;
    }
    return n;
  }

  // ------------------------------------------------------------------ fixed step
  /** `ambient` 关掉时只做归位（预感段 / 定格）。 */
  tick(dtMs: number, ambient: boolean): void {
    this.now += dtMs;
    for (const [id, slot] of this.slots) {
      if (slot.until > 0 && slot.until <= this.now) this.restore(id);
      if (!ambient || slot.talking || slot.until > this.now || !slot.seated || this.speaker === id) continue;
      if (this.now < slot.nextDrink) continue;
      slot.nextDrink = this.now + this.rng.range(DRINK_EVERY_MS[0], DRINK_EVERY_MS[1]);
      if (!this.hasState(slot, 'drink')) continue;
      slot.until = this.now + DRINK_MS;
      slot.actor.play('drink');
    }
  }

  // ------------------------------------------------------------------ debug
  /** id → 正在播放的动画 key（'' = 静止帧）。 */
  snapshot(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [id, slot] of this.slots) out[id] = (slot.actor as { currentAnimKey?: string }).currentAnimKey ?? slot.actor.sprite.anims.currentAnim?.key ?? '';
    return out;
  }
  /** id → 纹理 key（正式 `actor-*` 或开发占位）。 */
  textures(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [id, slot] of this.slots) out[id] = slot.actor.texture;
    return out;
  }
}
